import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.currentProject = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (this.socket?.connected) {
      console.log('✅ WebSocket already connected');
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    console.log(`🔌 Connecting to WebSocket server at ${apiUrl}`);

    this.socket = io(apiUrl, {
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts
    });

    // Connection event handlers
    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected:', this.socket.id);
      this.reconnectAttempts = 0;

      // Rejoin project room if we were in one
      if (this.currentProject) {
        this.joinProject(this.currentProject);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      this.reconnectAttempts = attemptNumber;
      console.log(`🔄 Reconnection attempt ${attemptNumber}/${this.maxReconnectAttempts}`);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ WebSocket reconnection failed');
    });

    // Set up event listeners for collection events
    this.setupEventListeners();
  }

  /**
   * Set up listeners for backend events
   */
  setupEventListeners() {
    if (!this.socket) return;

    // Remove any existing listeners first to prevent duplicates
    this.socket.off('collection:created');
    this.socket.off('collection:updated');
    this.socket.off('collection:deleted');
    this.socket.off('file:uploaded');
    this.socket.off('file:processing');
    this.socket.off('file:completed');
    this.socket.off('file:failed');
    this.socket.off('file:deleted');

    // Collection events
    this.socket.on('collection:created', (data) => {
      console.log('📥 Collection created:', data);
      this.notifyListeners('collection:created', data);
    });

    this.socket.on('collection:updated', (data) => {
      console.log('📥 Collection updated:', data);
      this.notifyListeners('collection:updated', data);
    });

    this.socket.on('collection:deleted', (data) => {
      console.log('📥 Collection deleted:', data);
      this.notifyListeners('collection:deleted', data);
    });

    // File events
    this.socket.on('file:uploaded', (data) => {
      console.log('📥 File uploaded:', data);
      this.notifyListeners('file:uploaded', data);
    });

    this.socket.on('file:processing', (data) => {
      console.log('📥 File processing:', data);
      this.notifyListeners('file:processing', data);
    });

    this.socket.on('file:completed', (data) => {
      console.log('📥 File completed:', data);
      this.notifyListeners('file:completed', data);
    });

    this.socket.on('file:failed', (data) => {
      console.log('📥 File failed:', data);
      this.notifyListeners('file:failed', data);
    });

    this.socket.on('file:deleted', (data) => {
      console.log('📥 File deleted:', data);
      this.notifyListeners('file:deleted', data);
    });
  }

  /**
   * Join a project room to receive its events
   */
  joinProject(projectId) {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot join project - WebSocket not connected');
      return;
    }

    // Leave previous project room if any
    if (this.currentProject && this.currentProject !== projectId) {
      this.leaveProject(this.currentProject);
    }

    this.currentProject = projectId;
    this.socket.emit('join:project', projectId);
    console.log(`📥 Joined project room: ${projectId}`);
  }

  /**
   * Leave a project room
   */
  leaveProject(projectId) {
    if (!this.socket?.connected) return;

    this.socket.emit('leave:project', projectId);
    console.log(`📤 Left project room: ${projectId}`);

    if (this.currentProject === projectId) {
      this.currentProject = null;
    }
  }

  /**
   * Subscribe to WebSocket events
   */
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(event, data) {
    const callbacks = this.listeners.get(event);
    if (!callbacks) return;

    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in WebSocket listener for ${event}:`, error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.currentProject = null;
      console.log('🔌 WebSocket disconnected');
    }
  }

  /**
   * Get connection status
   */
  isConnected() {
    return this.socket?.connected || false;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

// Auto-connect when service is imported
if (typeof window !== 'undefined') {
  websocketService.connect();
}

export default websocketService;